/* RESPONSIBLE TEAM: team-phone */
/* eslint-disable ember/no-controllers */

import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type FinVoiceService from 'embercom/services/fin-voice-service';
import type { Tab } from 'embercom/components/standalone/common/tab-control';

type DeployStep = Required<Tab> & {
  value: 'test' | 'intercom-phone';
};

export default class Deploy extends Controller {
  @service declare appService: $TSFixMe;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare finVoiceService: FinVoiceService;

  @tracked isSideMenuOpen = false;
  @tracked selectedStep: DeployStep | null = null;

  get selectedStepValue() {
    let currentRoute = this.router.currentRouteName.split('.').pop();
    if (['test', 'intercom-phone'].includes(currentRoute || '')) {
      return currentRoute;
    }
    return 'test';
  }

  get showSideMenuToggle() {
    return this.selectedStepValue === 'test';
  }

  get showSideMenu() {
    return this.showSideMenuToggle && this.isSideMenuOpen;
  }

  get hasSessions() {
    return !!this.finVoiceService.sessions?.length;
  }

  get deploySteps(): DeployStep[] {
    let steps = [
      {
        label: this.intl.t('ai-agent.deploy.phone-overview.voice-testing-tab'),
        value: 'test',
        path: 'apps.app.phone.standalone.deploy.test',
        show: true,
      },
      {
        label: this.intl.t('ai-agent.deploy.phone-overview.deploy-tab'),
        value: 'intercom-phone',
        path: 'apps.app.phone.standalone.deploy.intercom-phone',
        show: true,
      },
    ];

    return steps
      .filter((step) => step.show)
      .map((step, index) => {
        let tabProps = (({ show, ...tabProps }) => tabProps)(step);
        return {
          stepNumber: index + 1,
          ...tabProps,
        } as DeployStep;
      });
  }

  @action
  setStep(stepValue: DeployStep['value']) {
    this.selectedStep =
      this.deploySteps.find((step) => step.value === stepValue) || this.deploySteps[0];

    if (stepValue) {
      this.router.transitionTo(this.selectedStep.path);
    }
  }

  @action
  openSideMenu() {
    this.isSideMenuOpen = true;
  }

  @action
  closeSideMenu() {
    this.isSideMenuOpen = false;
  }

  @action
  copyLatestSessionId() {
    return this.finVoiceService.copyLatestSessionId();
  }

  @action
  toggleVerboseLogsEnabled() {
    this.finVoiceService.verboseLogsEnabled = !this.finVoiceService.verboseLogsEnabled;
  }
}

declare module '@ember/controller' {
  interface Registry {
    'apps/app/phone/standalone/deploy': Deploy;
  }
}
